import { cn } from '@/lib/cn';
import { trackButtonClicked, trackScreenViewed } from '@heidi/analytics/events';
import { ButtonAction, ScreenName } from '@heidi/analytics/vocabulary';
import { hasSeenTrialCompletionTooltipAtom } from '@heidi/commerce/data';
import {
  Spotlight,
  SpotlightAnchor,
  SpotlightArrow,
  SpotlightClose,
  SpotlightContent,
  SpotlightDescription,
  SpotlightHeader,
  SpotlightTitle,
} from '@heidi/design-system/spotlight';
import { useAtom } from 'jotai';
import { PartyPopper } from 'lucide-react';
import { type PropsWithChildren, useCallback, useEffect, useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

const messages = defineMessages({
  title: {
    defaultMessage: 'Congratulations!',
    id: 'Pa6bZXoqhW',
    description: 'Title of the spotlight shown when every trial-extension task is done',
  },
  close: {
    defaultMessage: 'Dismiss',
    id: 'pHNJ0Ec0SB',
    description: 'Accessible label for closing the trial-completion spotlight',
  },
  description: {
    defaultMessage:
      'You’ve completed all your tasks — we’ve added {days, plural, one {# free day} other {# free days}} to your trial.',
    id: 'Kxxslyf6ZA',
    description: 'Body of the trial-completion spotlight, with the number of days earned',
  },
});

type TrialCompletionTooltipProps = PropsWithChildren<{
  /** True once every gamified trial task is finished. Drives the one-time celebration. */
  allTasksComplete: boolean;
  /** Number of free trial days granted, surfaced in the celebration copy. */
  trialDays: number;
  /**
   * Width utility for the anchor wrapper. Defaults to `w-fit` (content-width,
   * for the icon-only ribbon button). Pass `w-full` when wrapping a full-width
   * nav item so it isn't collapsed to content width.
   */
  anchorClassName?: string;
}>;

/**
 * One-time celebratory tooltip anchored to the gamified-trial sidebar button.
 * Auto-opens the first time all trial tasks are complete, then persists a
 * "seen" flag so it never reappears.
 */
export const TrialCompletionTooltip = ({
  allTasksComplete,
  trialDays,
  anchorClassName = 'w-fit',
  children,
}: TrialCompletionTooltipProps) => {
  const intl = useIntl();
  const [hasSeen, setHasSeen] = useAtom(hasSeenTrialCompletionTooltipAtom);
  const hasTrackedViewRef = useRef(false);

  const shouldShow = allTasksComplete && !hasSeen;

  useEffect(() => {
    if (shouldShow && !hasTrackedViewRef.current) {
      trackScreenViewed({
        screen_name: ScreenName.TrialExtensionCompletionTooltip,
      });
      hasTrackedViewRef.current = true;
    }
  }, [shouldShow]);

  const handleDismiss = useCallback(() => {
    setHasSeen(true);
    trackButtonClicked({
      screenName: ScreenName.TrialExtensionCompletionTooltip,
      action: ButtonAction.TrialExtensionCompletionTooltipDismissed,
      label: 'Dismiss',
    });
  }, [setHasSeen]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) handleDismiss();
    },
    [handleDismiss]
  );

  return (
    <Spotlight open={shouldShow} onOpenChange={handleOpenChange}>
      <SpotlightAnchor asChild>
        <div className={cn(anchorClassName)}>{children}</div>
      </SpotlightAnchor>
      <SpotlightContent
        side="right"
        align="center"
        sideOffset={12}
        data-testid="trial-completion-spotlight"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SpotlightArrow />
        <SpotlightHeader icon={<PartyPopper aria-hidden />}>
          <SpotlightTitle>
            <FormattedMessage {...messages.title} />
          </SpotlightTitle>
          <SpotlightClose aria-label={intl.formatMessage(messages.close)} />
        </SpotlightHeader>
        <SpotlightDescription>
          <FormattedMessage {...messages.description} values={{ days: trialDays }} />
        </SpotlightDescription>
      </SpotlightContent>
    </Spotlight>
  );
};
